import React, { useState, useEffect, useRef } from 'react';
import PricingTable from './PricingTable';
import FloorPlans from './FloorPlans';
import ImageViewer from './ImageViewer';
import { dosyaAdiUret } from './imageDownload';
import { INTRO_CONTENT, LOCATION_URL, HOUSING_TYPES, PROJECT_INFO, FEATURES } from './constants';
import Img_1 from './assets/images/selim-sultan/1.jpeg';
import Img_2 from './assets/images/selim-sultan/2.jpeg';
import Img_3 from './assets/images/selim-sultan/3.jpeg';
import Img_4 from './assets/images/selim-sultan/4.jpeg';
import Img_5 from './assets/images/selim-sultan/5.jpeg';
import Img_6 from './assets/images/selim-sultan/6.jpeg';
import Img_7 from './assets/images/selim-sultan/7.jpeg';
import Img_8 from './assets/images/selim-sultan/8.jpeg';
import Img_9 from './assets/images/selim-sultan/9.jpeg';
import Img_10 from './assets/images/selim-sultan/10.jpeg';
import Img_11 from './assets/images/selim-sultan/11.jpeg';

// En düşük ön başvuru bedelinden başlayan aralık
const bedeller = HOUSING_TYPES.map(tip => tip.onBasvuruBedeli);
const bedelMetni = bedeller[bedeller.length - 1] + ' - ' + bedeller[0];

// Proje görselleri (kat planları ayrı bölümde, sabit olarak gösterilir)
const images = [Img_1, Img_2, Img_3, Img_4, Img_5, Img_6, Img_7, Img_8, Img_9, Img_10, Img_11]
  .map((src, sira) => ({
    src,
    label: `Proje Görseli ${sira + 1}`,
    dosyaAdi: `${dosyaAdiUret(`Selim Sultan Konakları Görsel ${sira + 1}`)}.jpeg`
  }));

const SWIPE_ESIGI = 40;

const StepIntro = ({ periodText, isOpen }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [otomatikGecis, setOtomatikGecis] = useState(true);
  const [viewerAcik, setViewerAcik] = useState(false);
  const dokunusRef = useRef(null);
  const swipeZamaniRef = useRef(0);

  // Kullanıcı karusele dokunduğunda veya görseli büyüttüğünde otomatik geçiş durur
  useEffect(() => {
    if (!otomatikGecis || viewerAcik) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [otomatikGecis, viewerAcik]);

  const slaytDegistir = (yon) => {
    setOtomatikGecis(false);
    setCurrentSlide((prev) => (prev + yon + images.length) % images.length);
  };

  const onTouchStart = (event) => {
    const dokunus = event.touches[0];
    dokunusRef.current = { x: dokunus.clientX, y: dokunus.clientY };
  };

  const onTouchEnd = (event) => {
    const baslangic = dokunusRef.current;
    dokunusRef.current = null;
    if (!baslangic) return;

    const dokunus = event.changedTouches[0];
    const dx = dokunus.clientX - baslangic.x;
    const dy = dokunus.clientY - baslangic.y;
    // Dikey kaydırmayı bozmamak için yalnızca belirgin yatay hareketler sayılır
    if (Math.abs(dx) < SWIPE_ESIGI || Math.abs(dx) <= Math.abs(dy)) return;

    swipeZamaniRef.current = Date.now();
    slaytDegistir(dx < 0 ? 1 : -1);
  };

  // Kaydırma sonrası gelen tıklama görüntüleyiciyi açmasın
  const onGorselClick = (index) => {
    if (Date.now() - swipeZamaniRef.current < 400) return;
    setOtomatikGecis(false);
    setCurrentSlide(index);
    setViewerAcik(true);
  };

  return (
    <div className="animate-fadeIn">
      <h2 className="text-xl font-bold text-gray-900 mb-3">
        {INTRO_CONTENT.title}
      </h2>

      {/* Hero Carousel */}
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="relative mb-8 rounded-xl overflow-hidden shadow-lg border-2 border-primary/10 group"
      >
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {images.map((gorsel, index) => (
            <img
              key={index}
              src={gorsel.src}
              alt={gorsel.label}
              onClick={() => onGorselClick(index)}
              className="w-full aspect-video object-cover shrink-0 cursor-pointer"
            />
          ))}
        </div>

        {/* Önceki / sonraki */}
        <button
          type="button"
          onClick={() => slaytDegistir(-1)}
          aria-label="Önceki görsel"
          className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 active:scale-95"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => slaytDegistir(1)}
          aria-label="Sonraki görsel"
          className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 active:scale-95"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Carousel Indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setOtomatikGecis(false);
                setCurrentSlide(index);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentSlide === index
                ? 'bg-primary w-2.5'
                : 'bg-white/50 hover:bg-white/80'
                }`}
            />
          ))}
        </div>

        <span className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm pointer-events-none">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 8v6m-3-3h6m5 0a8 8 0 11-16 0 8 8 0 0116 0z" />
          </svg>
          Büyütmek için dokunun
        </span>
      </div>

      {viewerAcik && (
        <ImageViewer
          images={images}
          startIndex={currentSlide}
          onClose={() => setViewerAcik(false)}
        />
      )}

      {/* Proje Konumu */}
      <a
        href={LOCATION_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full mb-8 bg-primary text-white py-3.5 px-6 rounded-xl font-semibold hover:bg-primary-dark transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.98]"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Konumu Göster
      </a>

      {/* Proje Künyesi */}
      {FEATURES.projeKunyesi && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { etiket: 'Toplam Alan', deger: PROJECT_INFO.toplamAlan },
            { etiket: 'Blok', deger: PROJECT_INFO.blokSayisi },
            { etiket: 'Daire', deger: PROJECT_INFO.daireSayisi }
          ].map((item) => (
            <div key={item.etiket} className="bg-primary-light border border-primary/20 rounded-xl p-3 text-center">
              <p className="text-base sm:text-lg font-bold text-primary leading-tight">{item.deger}</p>
              <p className="text-xs text-primary-text mt-0.5">{item.etiket}</p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4 text-gray-700 leading-relaxed">
        {INTRO_CONTENT.description.split('\n\n').map((paragraph, index) => (
          <p key={index} className="text-base md:text-lg">
            {paragraph}
          </p>
        ))}
      </div>

      {/* Konut tipleri ve ödeme tablosu */}
      <div className="mt-8">
        <h3 className="text-lg font-bold text-gray-900 mb-1">Konut Tipleri ve Ödeme Tablosu</h3>
        <p className="text-sm text-gray-600 mb-4">
          Projede {HOUSING_TYPES.length} konut tipi bulunmaktadır. Konut tipi seçimini bir sonraki adımda yapacaksınız.
        </p>
        <PricingTable />
      </div>

      {/* Kat planları — sabit, yakınlaştırılabilir ve indirilebilir */}
      <FloorPlans />

      <div className="mt-8 p-6 bg-primary-light rounded-lg border border-primary/20">
        <div className="flex items-start">
          <svg className="w-6 h-6 text-primary flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-4">
            <h3 className="font-semibold text-gray-900 mb-2">Önemli Bilgilendirme</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Ön başvuru sürecini tamamlamak için tüm adımları doldurmanız gerekmektedir.
              {periodText && (
                <span className="block mt-2 font-medium text-primary">
                  * Ön başvurular {periodText} tarihleri arasında alınmaktadır.
                  {isOpen === false && ' Şu anda ön başvuru dönemi kapalıdır.'}
                </span>
              )}
              <span className="block mt-2 font-medium text-primary">
                * Ön başvurular {HOUSING_TYPES.map(t => `${t.baslik} ${t.konsept} ${t.metrekare} (${t.daireSayisi} daire)`).join(', ')} olmak üzere {HOUSING_TYPES.length} konut tipi için alınmaktadır.
              </span>
              <span className="block mt-2 font-medium text-primary">
                * Ön başvuru bedeli konut tipine göre {bedelMetni} arasında değişmektedir; peşinat ve aidat bedelleri kesin üyelik sonrasında ayrıca alınacaktır.
              </span>
              <span className="block mt-2 font-medium text-primary">
                * Ön başvuru sayısı daire sayısını aşarsa kesin üyeler noter huzurunda kura ile belirlenecektir.
              </span>
              <span className="block mt-2 font-medium text-primary">
                * Her T.C. Kimlik Numarası ile yalnızca bir ön başvuru yapılabilir.
              </span>
              <span className="block mt-2 font-medium text-primary">
                * Ön başvurunuz alındıktan sonra bilgilendirme SMS'i gönderilecek, ödeme açıklamanız otomatik olarak üretilecektir.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepIntro;
